import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderKanban, LayoutGrid, List, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDeleteProject, useProjects, useUsers } from '../hooks/useApi';
import ProjectModal from '../components/ProjectModal';
import { canManageProjects } from '../utils/permissions';
import {
    AvatarGroup,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    PageHeader,
    Pagination,
    PriorityBadge,
    ProgressBar,
    StatusBadge,
    TableToolbar,
    useSort,
} from '../components/ui';

const PAGE_SIZE_DEFAULT = 15;

const formatDate = (value) => {
    if (!value || value === '0000-00-00') return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const teamNames = (project) => {
    const names = [];
    if (project.assignee_name) names.push(project.assignee_name);
    (project.team_members || []).forEach((m) => {
        if (m.full_name && m.full_name !== project.assignee_name) names.push(m.full_name);
    });
    return names;
};

const Projects = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [filters, setFilters] = useState({});
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [viewMode, setViewMode] = useState('list');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectToDelete, setProjectToDelete] = useState(null);

    const { data: result, isLoading } = useProjects({ limit: 1000 });
    const allProjects = useMemo(() => result?.data || [], [result]);

    const { data: usersResult } = useUsers({ limit: 1000 });
    const users = usersResult?.data || [];

    const deleteProject = useDeleteProject();
    const userCanManage = canManageProjects(user);
    const { sort, onSort } = useSort({ key: 'project_name', dir: 'asc' });

    /* --------------------------------------------------------- filter + sort */
    const filteredProjects = useMemo(() => {
        const term = search.trim().toLowerCase();

        const rows = allProjects.filter((project) => {
            if (filters.status && project.status !== filters.status) return false;
            if (filters.priority && project.priority !== filters.priority) return false;
            if (filters.assignee_id && String(project.assignee_id) !== String(filters.assignee_id)) return false;

            if (term) {
                const haystack = [
                    project.project_name,
                    project.project_code,
                    project.vendor,
                    project.department,
                    project.assignee_name,
                ];
                if (!haystack.some((v) => v?.toLowerCase().includes(term))) return false;
            }
            return true;
        });

        if (sort) {
            const dir = sort.dir === 'asc' ? 1 : -1;
            rows.sort((a, b) => {
                const av = a[sort.key];
                const bv = b[sort.key];
                if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
                return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true }) * dir;
            });
        }
        return rows;
    }, [allProjects, filters, search, sort]);

    const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const projects = filteredProjects.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    /* -------------------------------------------------------------- handlers */
    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const openCreate = () => {
        setSelectedProject(null);
        setIsModalOpen(true);
    };

    const openEdit = (project) => {
        setSelectedProject(project);
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!projectToDelete) return;
        try {
            await deleteProject.mutateAsync(projectToDelete);
            setProjectToDelete(null);
        } catch (error) {
            console.error('Error deleting project:', error);
        }
    };

    const hasFilters = Boolean(search || filters.status || filters.priority || filters.assignee_id);

    /* --------------------------------------------------------------- columns */
    const columns = [
        {
            key: 'project_name',
            header: 'Project',
            sortable: true,
            render: (p) => (
                <Link to={`/projects/${p.id}`} className="block group" onClick={(e) => e.stopPropagation()}>
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[280px]">
                        {p.project_name}
                    </span>
                    {p.project_code && (
                        <span className="block text-micro text-ink-500 font-mono mt-0.5">{p.project_code}</span>
                    )}
                </Link>
            ),
        },
        {
            key: 'vendor',
            header: 'Vendor / Dept.',
            sortable: true,
            render: (p) => (
                <>
                    <span className="block text-ink-700 truncate max-w-[180px]">{p.vendor || '—'}</span>
                    <span className="block text-micro text-ink-500">{p.department || 'General'}</span>
                </>
            ),
        },
        {
            key: 'assignee_name',
            header: 'Team',
            render: (p) => {
                const names = teamNames(p);
                if (!names.length) return <span className="text-muted">Unassigned</span>;
                return (
                    <div className="flex items-center gap-2">
                        <AvatarGroup names={names} max={3} />
                        <span className="text-ink-700 truncate max-w-[130px]">{names[0]}</span>
                    </div>
                );
            },
        },
        {
            key: 'priority',
            header: 'Priority',
            sortable: true,
            render: (p) => <PriorityBadge priority={p.priority} />,
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (p) => <StatusBadge status={p.status} />,
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: true,
            width: 150,
            render: (p) => <ProgressBar value={p.progress} showValue />,
        },
        {
            key: 'end_date',
            header: 'Due',
            sortable: true,
            render: (p) => <span className="text-ink-600 tabular whitespace-nowrap">{formatDate(p.end_date)}</span>,
        },
    ];

    if (userCanManage) {
        columns.push({
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (p) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown label={`Actions for ${p.project_name}`}>
                        <MenuItem icon={Pencil} onClick={() => openEdit(p)}>
                            Edit project
                        </MenuItem>
                        <MenuItem icon={Trash2} danger onClick={() => setProjectToDelete(p.id)}>
                            Delete project
                        </MenuItem>
                    </Dropdown>
                </div>
            ),
        });
    }

    /* ---------------------------------------------------------------- render */
    return (
        <>
            <PageHeader
                title="Projects"
                description="Delivery portfolio across vendors, departments and project managers."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Projects' }]}
                actions={
                    userCanManage && (
                        <Button variant="primary" icon={Plus} onClick={openCreate}>
                            New project
                        </Button>
                    )
                }
            />

            <Card>
                <TableToolbar
                    search={search}
                    onSearchChange={(v) => {
                        setSearch(v);
                        setPage(1);
                    }}
                    searchPlaceholder="Search name, code, vendor…"
                    filters={
                        <>
                            <select
                                className="input w-auto"
                                aria-label="Filter by status"
                                value={filters.status || ''}
                                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                            >
                                <option value="">All statuses</option>
                                <option value="planned">Planned</option>
                                <option value="in_progress">In progress</option>
                                <option value="on_hold">On hold</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by priority"
                                value={filters.priority || ''}
                                onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                            >
                                <option value="">All priorities</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by project manager"
                                value={filters.assignee_id || ''}
                                onChange={(e) => handleFilterChange('assignee_id', e.target.value || undefined)}
                            >
                                <option value="">All managers</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </option>
                                ))}
                            </select>
                        </>
                    }
                    actions={
                        <div className="inline-flex items-center gap-0.5 p-0.5 bg-ink-100 rounded-md">
                            {[
                                { key: 'list', icon: List, label: 'List view' },
                                { key: 'grid', icon: LayoutGrid, label: 'Grid view' },
                            ].map(({ key, icon: Icon, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setViewMode(key)}
                                    aria-pressed={viewMode === key}
                                    aria-label={label}
                                    title={label}
                                    className={`h-[30px] w-[30px] inline-flex items-center justify-center rounded-sm transition-colors duration-150 ${viewMode === key
                                        ? 'bg-white text-ink-900 shadow-xs'
                                        : 'text-ink-500 hover:text-ink-800'
                                        }`}
                                >
                                    <Icon size={15} aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    }
                />

                {viewMode === 'list' ? (
                    <DataTable
                        columns={columns}
                        rows={projects}
                        loading={isLoading}
                        sort={sort}
                        onSort={onSort}
                        onRowClick={(p) => navigate(`/projects/${p.id}`)}
                        empty={
                            <EmptyState
                                icon={FolderKanban}
                                title={hasFilters ? 'No projects match these filters' : 'No projects yet'}
                                description={
                                    hasFilters
                                        ? 'Clear the search or filters to see the full portfolio.'
                                        : userCanManage
                                            ? 'Create the first project to start tracking delivery.'
                                            : 'No projects have been assigned to you yet.'
                                }
                                action={
                                    hasFilters ? (
                                        <Button
                                            onClick={() => {
                                                setSearch('');
                                                setFilters({});
                                            }}
                                        >
                                            Clear filters
                                        </Button>
                                    ) : (
                                        userCanManage && (
                                            <Button variant="primary" icon={Plus} onClick={openCreate}>
                                                New project
                                            </Button>
                                        )
                                    )
                                }
                            />
                        }
                    />
                ) : (
                    <div className="p-4">
                        {projects.length ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                                {projects.map((project) => (
                                    <Link
                                        key={project.id}
                                        to={`/projects/${project.id}`}
                                        className="card card-hover p-4 flex flex-col gap-3"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <h3 className="text-md font-semibold text-ink-900 truncate">
                                                    {project.project_name}
                                                </h3>
                                                <p className="text-micro text-ink-500 font-mono mt-0.5">
                                                    {project.project_code || '—'}
                                                </p>
                                            </div>
                                            <PriorityBadge priority={project.priority} className="shrink-0" />
                                        </div>

                                        {project.description && (
                                            <p className="text-base text-ink-600 line-clamp-2">{project.description}</p>
                                        )}

                                        <dl className="text-label text-ink-600 space-y-1">
                                            <div className="flex gap-2">
                                                <dt className="text-ink-500 w-16 shrink-0">Vendor</dt>
                                                <dd className="truncate">{project.vendor || '—'}</dd>
                                            </div>
                                            <div className="flex gap-2">
                                                <dt className="text-ink-500 w-16 shrink-0">Timeline</dt>
                                                <dd className="tabular">
                                                    {formatDate(project.start_date)} – {formatDate(project.end_date)}
                                                </dd>
                                            </div>
                                        </dl>

                                        <ProgressBar value={project.progress} showValue className="mt-auto" />

                                        <div className="flex items-center justify-between gap-2 pt-3 border-t border-[var(--divider-color)]">
                                            <StatusBadge status={project.status} />
                                            <div className="flex items-center gap-2">
                                                <span className="text-micro text-ink-500 tabular">
                                                    {project.completed_tasks || 0}/{project.task_count || 0} tasks
                                                </span>
                                                <AvatarGroup names={teamNames(project)} max={2} />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <EmptyState
                                icon={FolderKanban}
                                title={hasFilters ? 'No projects match these filters' : 'No projects yet'}
                                description={
                                    hasFilters
                                        ? 'Clear the search or filters to see the full portfolio.'
                                        : 'Create the first project to start tracking delivery.'
                                }
                            />
                        )}
                    </div>
                )}

                {filteredProjects.length > 0 && (
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        total={filteredProjects.length}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={(n) => {
                            setPageSize(n);
                            setPage(1);
                        }}
                    />
                )}
            </Card>

            {isModalOpen && (
                <ProjectModal project={selectedProject} onClose={() => setIsModalOpen(false)} />
            )}

            <ConfirmDialog
                isOpen={Boolean(projectToDelete)}
                title="Delete project"
                message="This permanently removes the project and everything linked to it. This cannot be undone."
                confirmText="Delete project"
                variant="danger"
                isLoading={deleteProject.isPending}
                onConfirm={confirmDelete}
                onClose={() => setProjectToDelete(null)}
            />
        </>
    );
};

export default Projects;
